/**
 * Local filesystem text import (.md / .txt) - allowlist, size, and createDocument batch.
 * See docs/product/bets/2026-07-10-local-text-document-import.md
 */

import { slugify } from '@/lib/api/documents'

export const LOCAL_TEXT_IMPORT_MAX_BYTES = 2 * 1024 * 1024
/** Max attempts when preferred import path already exists (suffix -2, -3, ...). */
const IMPORT_PATH_UNIQUENESS_MAX = 40

/** File input accept attribute */
export const LOCAL_TEXT_IMPORT_ACCEPT = '.md,.txt,text/plain,text/markdown'

const ALLOWED_EXTENSIONS = new Set(['md', 'txt'])
const ALLOWED_MIME = new Set([
  'text/plain',
  'text/markdown',
  'text/x-markdown',
  'application/markdown',
])

export type LocalTextImportCheck =
  | { ok: true }
  | { ok: false; reason: string }

export function extensionOfFilename(filename: string): string {
  const base = filename.split(/[/\\]/).pop() ?? filename
  const i = base.lastIndexOf('.')
  if (i <= 0 || i === base.length - 1) return ''
  return base.slice(i + 1).toLowerCase()
}

/** Title for createDocument: filename stem without extension. */
export function titleFromImportFilename(filename: string): string {
  const base = (filename.split(/[/\\]/).pop() ?? filename).trim()
  const i = base.lastIndexOf('.')
  const stem = i > 0 ? base.slice(0, i) : base
  return stem.trim() || 'Imported note'
}

/**
 * Parent folder for a document file_path (sibling import target).
 * `hop-studios/a/b/note.md` -> `hop-studios/a/b`. Root-level files return ''.
 */
export function parentFolderFromDocumentPath(filePath: string | null | undefined): string | null {
  if (!filePath || typeof filePath !== 'string') return null
  const normalized = filePath.replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, '')
  if (!normalized) return null
  const idx = normalized.lastIndexOf('/')
  if (idx < 0) return ''
  return normalized.slice(0, idx)
}

/**
 * Landing folder for chat long-paste / .md drop when scope is a workspace content root.
 * Prevents dumping notes at Documents root (`hop-studios/foo.md`).
 * Interim until session scratch (`system/sessions/{id}/`) ships - see
 * docs/product/bets/2026-07-31-chat-session-scratch-and-transient-memory.md
 */
export function resolveChatImportLandingFolder(folderPath: string): string {
  const folder = (folderPath || '').replace(/^\/+/, '').replace(/\/+$/, '')
  if (!folder) return 'inbox'
  // Content root only (single path segment, e.g. hop-studios) -> {root}/inbox
  if (!folder.includes('/')) return `${folder}/inbox`
  return folder
}

/**
 * Content-relative file_path for an import. Always `.md` (notes surface).
 * uniquenessIndex 0 -> `{stem}.md`; 1 -> `{stem}-2.md`; etc.
 */
export function filePathForLocalTextImport(
  folderPath: string,
  sourceFilenameOrTitle: string,
  uniquenessIndex = 0,
): string {
  const raw = (sourceFilenameOrTitle || '').trim()
  const looksLikeFilename = /\.(md|txt)$/i.test(raw)
  const title = looksLikeFilename ? titleFromImportFilename(raw) : raw || 'Imported note'
  let stem = slugify(title) || 'imported-note'
  if (uniquenessIndex > 0) {
    stem = `${stem}-${uniquenessIndex + 1}`
  }
  const folder = (folderPath || '').replace(/^\/+/, '').replace(/\/+$/, '')
  const fname = `${stem}.md`
  return folder ? `${folder}/${fname}` : fname
}

function isPathConflictError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err)
  return /already exists|conflict|409/i.test(msg)
}

export function isLocalTextImportFile(file: {
  name: string
  type?: string
  size?: number
}): LocalTextImportCheck {
  const ext = extensionOfFilename(file.name)
  const mime = (file.type || '').toLowerCase().trim()

  if (!ALLOWED_EXTENSIONS.has(ext)) {
    return {
      ok: false,
      reason: `Only .md and .txt files can be imported (got ${ext ? `.${ext}` : 'unknown type'})`,
    }
  }

  if (mime && !ALLOWED_MIME.has(mime) && mime !== 'application/octet-stream') {
    return {
      ok: false,
      reason: `Unsupported type for ${file.name}`,
    }
  }

  if (typeof file.size === 'number' && file.size > LOCAL_TEXT_IMPORT_MAX_BYTES) {
    const mb = (LOCAL_TEXT_IMPORT_MAX_BYTES / (1024 * 1024)).toFixed(0)
    return {
      ok: false,
      reason: `${file.name} is too large (max ${mb}MB)`,
    }
  }

  return { ok: true }
}

export function partitionLocalTextImportFiles(files: File[]): {
  textFiles: File[]
  otherFiles: File[]
  rejected: Array<{ name: string; reason: string }>
} {
  const textFiles: File[] = []
  const otherFiles: File[] = []
  const rejected: Array<{ name: string; reason: string }> = []

  for (const file of files) {
    const ext = extensionOfFilename(file.name)
    if (ALLOWED_EXTENSIONS.has(ext) || file.type.startsWith('text/')) {
      const check = isLocalTextImportFile(file)
      if (check.ok) {
        textFiles.push(file)
      } else {
        rejected.push({ name: file.name, reason: check.reason })
      }
    } else {
      otherFiles.push(file)
    }
  }

  return { textFiles, otherFiles, rejected }
}

export async function readLocalTextImportFile(file: File): Promise<string> {
  const check = isLocalTextImportFile(file)
  if (!check.ok) {
    throw new Error(check.reason)
  }

  let text: string
  try {
    text = await file.text()
  } catch {
    throw new Error(`Could not read ${file.name} as text`)
  }

  // Reject if content looks binary (NUL bytes)
  if (text.includes('\u0000')) {
    throw new Error(`${file.name} does not look like a text file`)
  }

  return text
}

export type CreateDocumentFn = (doc: {
  title?: string
  content?: string
  document_type?: string
  folder_path?: string
  workspace_id?: string
  file_path?: string
}) => Promise<{
  success?: boolean
  document: { id: string; title: string; filePath?: string }
}>

async function createDocumentWithPreferredPath(
  createDocument: CreateDocumentFn,
  options: {
    title: string
    content: string
    folderPath: string
    workspaceId?: string
    /** Source filename or title stem used to build file_path */
    pathBasis: string
  },
): Promise<{ id: string; title: string; filePath?: string }> {
  let lastError: unknown
  for (let i = 0; i < IMPORT_PATH_UNIQUENESS_MAX; i++) {
    const filePath = filePathForLocalTextImport(options.folderPath, options.pathBasis, i)
    try {
      const result = await createDocument({
        title: options.title,
        content: options.content,
        document_type: 'note',
        folder_path: options.folderPath,
        file_path: filePath,
        ...(options.workspaceId ? { workspace_id: options.workspaceId } : {}),
      })
      if (!result.document?.id) {
        throw new Error('Server did not return a document id')
      }
      return {
        id: result.document.id,
        title: result.document.title || options.title,
        filePath: result.document.filePath || filePath,
      }
    } catch (err) {
      lastError = err
      if (!isPathConflictError(err)) throw err
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error('Could not allocate a unique import filename')
}

export type ImportedLocalTextDoc = {
  id: string
  title: string
  filePath?: string
  sourceName: string
}

/**
 * Import allowlisted text files as FlowNotes notes via createDocument.
 */
export async function importLocalTextFiles(options: {
  files: File[]
  folderPath: string
  workspaceId?: string
  createDocument: CreateDocumentFn
}): Promise<{
  imported: ImportedLocalTextDoc[]
  errors: Array<{ name: string; reason: string }>
}> {
  const imported: ImportedLocalTextDoc[] = []
  const errors: Array<{ name: string; reason: string }> = []

  for (const file of options.files) {
    const check = isLocalTextImportFile(file)
    if (!check.ok) {
      errors.push({ name: file.name, reason: check.reason })
      continue
    }
    try {
      const content = await readLocalTextImportFile(file)
      const title = titleFromImportFilename(file.name)
      const doc = await createDocumentWithPreferredPath(options.createDocument, {
        title,
        content,
        folderPath: options.folderPath,
        workspaceId: options.workspaceId,
        pathBasis: file.name,
      })
      imported.push({
        id: doc.id,
        title: doc.title,
        filePath: doc.filePath,
        sourceName: file.name,
      })
    } catch (err) {
      errors.push({
        name: file.name,
        reason: err instanceof Error ? err.message : 'Import failed',
      })
    }
  }

  return { imported, errors }
}

/** Open a hidden file picker for .md / .txt. Resolves [] if cancelled. */
export function pickLocalTextImportFiles(multiple = true): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = LOCAL_TEXT_IMPORT_ACCEPT
    input.multiple = multiple
    input.style.display = 'none'
    let settled = false
    const finish = (files: File[]) => {
      if (settled) return
      settled = true
      input.remove()
      resolve(files)
    }
    input.addEventListener('change', () => {
      finish(input.files ? Array.from(input.files) : [])
    })
    // Cancel: no change event; resolve empty after window regains focus
    window.addEventListener(
      'focus',
      () => {
        window.setTimeout(() => finish([]), 500)
      },
      { once: true },
    )
    document.body.appendChild(input)
    input.click()
  })
}

/** Long paste: convert to a FlowNotes doc + chip instead of inlining (plan 2026-07-13). */
export const LONG_TEXT_PASTE_CHAR_THRESHOLD = 3000
export const LONG_TEXT_PASTE_LINE_THRESHOLD = 40

export function isLongTextPaste(text: string): boolean {
  const trimmed = text.trim()
  if (!trimmed) return false
  if (trimmed.length >= LONG_TEXT_PASTE_CHAR_THRESHOLD) return true
  return trimmed.split('\n').length >= LONG_TEXT_PASTE_LINE_THRESHOLD
}

/** Word/Excel pastes often lead with an orphan quote or markdown hr (`---`). */
const PASTE_TITLE_JUNK_LINE =
  /^[\s'"\u2018\u2019\u201c\u201d`\u2013\u2014*#=_.,;:/\\|~-]+$/

function unwrapPairedQuotes(s: string): string {
  const pairs: Array<[string, string]> = [
    ['"', '"'],
    ["'", "'"],
    ['\u201c', '\u201d'],
    ['\u2018', '\u2019'],
  ]
  for (const [open, close] of pairs) {
    if (s.length >= 2 && s.startsWith(open) && s.endsWith(close)) {
      return s.slice(open.length, s.length - close.length).trim()
    }
  }
  return s
}

function pastedTimestampTitle(now: Date): string {
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  const hh = String(now.getHours()).padStart(2, '0')
  const mm = String(now.getMinutes()).padStart(2, '0')
  return `pasted-${y}-${m}-${d}-${hh}${mm}`
}

/**
 * Title for pasted blob: first meaningful line truncated, or pasted-YYYY-MM-DD-HHMM.
 * Skips quote-only / hr lines so the chat chip is not labeled `'`.
 */
export function titleFromPastedText(text: string, now = new Date()): string {
  for (const raw of text.split('\n')) {
    let line = raw.trim().replace(/^#+\s*/, '')
    line = unwrapPairedQuotes(line)
    if (!line) continue
    if (PASTE_TITLE_JUNK_LINE.test(line)) continue
    if (/^(?:-{3,}|\*{3,}|_{3,}|=+)$/.test(line)) continue
    const cleaned = line.slice(0, 60).trim()
    if (cleaned) return cleaned
  }
  return pastedTimestampTitle(now)
}

/**
 * Create a .txt note from a long clipboard paste (same createDocument path as file import).
 */
export async function importPastedTextAsDocument(options: {
  text: string
  folderPath: string
  workspaceId?: string
  createDocument: CreateDocumentFn
  title?: string
}): Promise<ImportedLocalTextDoc> {
  const trimmed = options.text.trim()
  if (!trimmed) {
    throw new Error('Paste is empty')
  }
  const byteLength = new TextEncoder().encode(trimmed).length
  if (byteLength > LOCAL_TEXT_IMPORT_MAX_BYTES) {
    const mb = (LOCAL_TEXT_IMPORT_MAX_BYTES / (1024 * 1024)).toFixed(0)
    throw new Error(`Paste is too large (max ${mb}MB)`)
  }
  const title = options.title?.trim() || titleFromPastedText(trimmed)
  const doc = await createDocumentWithPreferredPath(options.createDocument, {
    title,
    content: trimmed,
    folderPath: options.folderPath,
    workspaceId: options.workspaceId,
    pathBasis: title,
  })
  return {
    id: doc.id,
    title: doc.title,
    filePath: doc.filePath,
    sourceName: 'paste',
  }
}
